import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ChevronRight, ChevronDown, Loader2, ShoppingBag, CheckCircle2, AlertTriangle, Package, Sparkles, ShieldCheck, Truck, Download, FileText, Info, PenTool } from "lucide-react";
import { toast } from "sonner";
import useSEO from "@/hooks/useSEO";
import { useCart } from "@/lib/cart";
import { getProductImage, getCategoryContent, getProductContent, SHOP_TRUST, FILE_PREP_STEPS, FILE_PREP_GUIDELINES, getProductSpecs } from "@/lib/shop-content";
import ArtworkUploader from "@/components/ArtworkUploader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRICE_DEBOUNCE_MS = 400;

/**
 * Product detail — SinaLite-style layout:
 *   Hero: [product image | title + tagline + configurator + live price]
 *   Below: description, features grid, trust strip
 */
export default function ShopProduct() {
  const { id } = useParams();
  const navigate = useNavigate();
  const productId = parseInt(id, 10);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selection, setSelection] = useState({});
  const [price, setPrice] = useState(null);
  const [pricing, setPricing] = useState(false);
  const [priceError, setPriceError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [related, setRelated] = useState([]);
  const [artwork, setArtwork] = useState(null); // {artwork_token, artwork_filename, artwork_size} or null
  const { addItem } = useCart();

  const heroImage = product ? getProductImage(product) : null;

  const jsonLd = useMemo(() => {
    if (!product) return null;
    const name = (product.name || "").trim();
    const content = getProductContent(product);
    const img = heroImage && heroImage.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${heroImage}` : heroImage;
    const productUrl = `${window.location.origin}/shop/product/${product.id}`;
    const productSchema = {
      "@context": "https://schema.org",
      "@type": "Product",
      name,
      sku: product.sku,
      category: product.category,
      description: content.description,
      image: img ? [img] : undefined,
      brand: { "@type": "Brand", name: "Toronto Custom Signs & Wraps" },
      url: productUrl,
    };
    if (price && price.total != null) {
      productSchema.offers = {
        "@type": "Offer",
        priceCurrency: price.currency || "CAD",
        price: Number(price.total).toFixed(2),
        availability: "https://schema.org/InStock",
        url: productUrl,
        seller: { "@type": "Organization", name: "Toronto Custom Signs & Wraps" },
      };
    }

    // BreadcrumbList — helps Google show a rich breadcrumb path (Home ›
    // Shop › Category › Product) under the result title, which measurably
    // lifts CTR on category-heavy sites.
    const origin = window.location.origin;
    const crumbs = [
      { name: "Home", item: `${origin}/` },
      { name: "Shop", item: `${origin}/shop` },
    ];
    if (product.category) {
      crumbs.push({
        name: product.category,
        item: `${origin}/shop?category=${encodeURIComponent(product.category)}`,
      });
    }
    crumbs.push({ name, item: productUrl });
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: crumbs.map((c, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: c.name,
        item: c.item,
      })),
    };

    return [productSchema, breadcrumbSchema];
  }, [product, price, heroImage]);

  useSEO({
    title: product ? `${(product.name || "").trim()} — Order Online` : "Print Shop",
    description: product ? `Configure and order ${(product.name || "").trim()} online with live pricing and Canada-wide shipping.` : undefined,
    path: `/shop/product/${id}`,
    image: heroImage,
    type: "product",
    jsonLd,
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setPriceError(null);
    setPrice(null);
    axios.get(`${API}/sinalite/products/${productId}`).then(({ data }) => {
      if (cancelled) return;
      setProduct(data);
      const groups = (data.options && data.options.option_groups) || {};
      const initial = {};
      Object.entries(groups).forEach(([g, opts]) => { if (opts && opts.length) initial[g] = opts[0].id; });
      setSelection(initial);
    }).catch(() => { if (!cancelled) setProduct(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    // Related products — fires in parallel with the main product fetch.
    // Failures are silently swallowed: an empty carousel is preferable to a
    // toast on a page load.
    axios.get(`${API}/sinalite/products/${productId}/related?limit=4`)
      .then(({ data }) => { if (!cancelled) setRelated(data.items || []); })
      .catch(() => { if (!cancelled) setRelated([]); });
    return () => { cancelled = true; };
  }, [productId]);

  const optionIds = useMemo(() => Object.values(selection).filter((v) => v != null), [selection]);
  const selectionKey = optionIds.join(",");

  useEffect(() => {
    if (!product || optionIds.length === 0) return;
    setPriceError(null);
    setPricing(true);
    const handle = setTimeout(async () => {
      try {
        const { data } = await axios.post(`${API}/sinalite/price/${productId}`, { option_ids: optionIds });
        setPrice(data);
        if (data?.warning) setPriceError(data.warning);
      } catch (err) {
        setPrice(null);
        setPriceError(err.response?.data?.detail?.error || "Could not fetch price for this configuration.");
      } finally { setPricing(false); }
    }, PRICE_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [productId, selectionKey, product, optionIds]);

  const handleSelect = useCallback((group, id) => setSelection((s) => ({ ...s, [group]: id })), []);

  const handleAddToCart = () => {
    if (!price || price.total == null) { toast.error("Please wait for the price to load."); return; }
    addItem({
      product_id: productId, product_name: product.name.trim(), category: product.category, sku: product.sku,
      option_ids: optionIds, selection: price.selection || {}, unit_price: price.total,
      currency: price.currency || "CAD", quantity, package_info: price.package_info || null,
      // Carry the pre-uploaded artwork through to checkout so create-payment
      // can attach it to the order automatically.
      artwork_token: artwork?.artwork_token || null,
      artwork_filename: artwork?.artwork_filename || null,
      artwork_size: artwork?.artwork_size || null,
    });
    toast.success("Added to cart", {
      description: `${quantity} × ${product.name.trim()} — $${(price.total * quantity).toFixed(2)} ${price.currency || "CAD"}${artwork ? " · artwork attached" : ""}`,
      action: { label: "View cart", onClick: () => navigate("/cart") },
    });
    // Clear the local artwork ref so a subsequent add doesn't re-use the same
    // token (it now belongs to a cart line).
    setArtwork(null);
  };

  if (loading) {
    return (
      <div className="pt-28 pb-24 bg-ink-950 min-h-screen">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div className="aspect-square bg-ink-900 border border-white/5 animate-pulse rounded-sm" />
            <div className="space-y-4">
              <div className="h-8 w-1/2 bg-ink-900 animate-pulse rounded-sm" />
              <div className="h-16 bg-ink-900 animate-pulse rounded-sm" />
              <div className="h-64 bg-ink-900 animate-pulse rounded-sm" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="pt-28 pb-24 bg-ink-950 min-h-screen" data-testid="product-not-found">
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center">
          <div className="eyebrow gold-bar mx-auto w-fit">404</div>
          <h1 className="font-display text-4xl mt-4">Product not available</h1>
          <p className="text-white/60 mt-4">This product isn&apos;t live on our shop yet.</p>
          <Link to="/shop" className="btn-gold mt-8 inline-flex">← Back to shop</Link>
        </div>
      </div>
    );
  }

  const groups = (product.options && product.options.option_groups) || {};
  const groupNames = Object.keys(groups);
  const metaFlags = (product.options && product.options.meta_flags) || [];
  const hasCustomSize = metaFlags.includes("custom_size");
  const catContent = getProductContent(product);

  return (
    <div className="bg-ink-950 min-h-screen" data-testid={`shop-product-${productId}`}>
      {/* HERO — image + configurator */}
      <section className="relative overflow-hidden pt-28 pb-14 border-b border-white/5">
        <div className="absolute inset-0 bg-gradient-to-b from-ink-900/40 via-ink-950 to-ink-950 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <nav className="text-[10px] uppercase tracking-[0.24em] text-white/40 mb-6 flex items-center gap-2 flex-wrap">
            <Link to="/" className="hover:text-gold">Home</Link>
            <ChevronRight size={11} />
            <Link to="/shop" className="hover:text-gold">Print Shop</Link>
            <ChevronRight size={11} />
            <Link to={`/shop?category=${encodeURIComponent(product.category)}`} className="hover:text-gold">{product.category}</Link>
            <ChevronRight size={11} />
            <span className="text-gold truncate max-w-[240px]">{product.name.trim()}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,480px)] gap-10 lg:gap-14">
            {/* Image — single hero photo per product; no duplicated thumbnails. */}
            <div>
              <div className="aspect-[4/3] bg-ink-900 border border-white/5 rounded-sm overflow-hidden relative">
                <img src={heroImage} alt={product.seo_alt_text || product.name} data-testid="product-hero-image" className="w-full h-full object-contain p-10" onError={(e) => { e.currentTarget.parentElement.classList.add("bg-gradient-to-br","from-ink-800","to-ink-950"); e.currentTarget.style.display = "none"; }} />
              </div>
            </div>

            {/* Title + Configurator + Price */}
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-gold">{product.category}</div>
              <h1 className="font-display text-3xl md:text-4xl mt-3 tracking-tight leading-tight" data-testid="product-title">
                {product.name.trim()}
              </h1>
              <p className="text-white/60 text-sm mt-4 leading-relaxed">{catContent.tagline}. Configure your job below to see live pricing.</p>
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mt-3">#{product.id} · {product.sku}</div>

              {hasCustomSize && (
                <div className="mt-6 flex items-start gap-3 border border-gold/30 bg-gold/5 p-4 rounded-sm">
                  <AlertTriangle size={16} className="text-gold shrink-0 mt-0.5" />
                  <div className="text-sm text-white/75">
                    Custom sizes available — <Link to="/contact" className="underline hover:text-gold">contact us</Link> for a manual quote.
                  </div>
                </div>
              )}

              <div className="mt-8 space-y-6">
                {groupNames.length === 0 ? (
                  <div className="card-dark p-6 text-white/55 text-sm">Options are still loading. Please try again in a moment.</div>
                ) : groupNames.map((g) => (
                  <OptionGroup key={g} name={g} options={groups[g]} selected={selection[g]} onSelect={(id) => handleSelect(g, id)} />
                ))}
              </div>

              {/* Price panel */}
              <div className="mt-8 card-dark p-6 border-gold/20" data-testid="price-panel">
                <div className="flex items-baseline justify-between">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/50">Your price</div>
                  {price?.selection && Object.keys(price.selection).length > 0 && (
                    <div className="text-[10px] uppercase tracking-[0.18em] text-white/40">Live</div>
                  )}
                </div>
                <div className="mt-2 flex items-baseline gap-3 min-h-[52px]">
                  {pricing ? (
                    <span className="text-white/60 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Calculating…</span>
                  ) : priceError ? (
                    <span className="text-red-300 text-sm">Price unavailable — try a different option</span>
                  ) : price && price.total != null ? (
                    <>
                      <span className="font-display text-4xl text-gold" data-testid="price-total">${price.total.toFixed(2)}</span>
                      <span className="text-white/50 text-sm uppercase tracking-[0.2em]">{price.currency || "CAD"}</span>
                    </>
                  ) : (
                    <span className="text-white/50 text-sm">Select options to see price</span>
                  )}
                </div>

                <div className="mt-5 flex items-center gap-3">
                  <label className="text-[11px] uppercase tracking-[0.22em] text-white/55">Sets</label>
                  <input data-testid="product-quantity" type="number" min={1} value={quantity} onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value, 10) || 1))} className="w-20 bg-ink-800 border border-white/10 text-white px-3 py-2 text-sm rounded-sm outline-none focus:border-gold" />
                  <span className="text-white/40 text-xs">× ${price?.total ? price.total.toFixed(2) : "—"}</span>
                </div>

                {/* ARTWORK UPLOAD — buyers with a ready file can attach it now
                    and skip the post-checkout email step entirely. */}
                <div className="mt-5">
                  <ArtworkUploader value={artwork} onChange={setArtwork} />
                </div>

                <button onClick={handleAddToCart} disabled={!price || pricing || price.total == null} data-testid="add-to-cart-btn" className="btn-gold w-full justify-center mt-5 disabled:opacity-40 disabled:cursor-not-allowed">
                  <ShoppingBag size={14} /> Add to cart{artwork ? " + artwork" : ""}
                </button>

                {price?.package_info && (
                  <div className="mt-5 pt-4 border-t border-white/5 text-[11px] text-white/50 flex items-center gap-2">
                    <Package size={12} className="text-gold" />
                    Ships in {price.package_info["number of boxes"] || "1"} box(es) · {price.package_info["box size"] || "—"}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TABBED CONTENT — Details | File Prep | About */}
      <ProductTabs product={product} catContent={catContent} />

      {/* RELATED PRODUCTS — internal linking + AOV lift */}
      {related.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-10 py-16 border-t border-white/5" data-testid="related-products">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="eyebrow gold-bar">More in {product.category}</div>
              <h2 className="font-display text-3xl md:text-4xl mt-3 tracking-tight">Related products</h2>
            </div>
            <Link
              to={`/shop?category=${encodeURIComponent(product.category)}`}
              data-testid="related-view-category"
              className="text-[11px] uppercase tracking-[0.22em] text-white/60 hover:text-gold flex items-center gap-1"
            >
              View all <ChevronRight size={12} />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {related.map((r) => {
              const img = getProductImage(r);
              return (
                <Link
                  key={r.id}
                  to={`/shop/product/${r.id}`}
                  data-testid={`related-product-${r.id}`}
                  className="card-dark overflow-hidden hover:border-gold/50 transition-colors group flex flex-col"
                >
                  <div className="aspect-[4/3] bg-ink-800 relative overflow-hidden">
                    <img
                      src={img}
                      alt={r.seo_alt_text || r.name}
                      className="w-full h-full object-contain p-6 group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4 flex-1 flex flex-col">
                    <div className="font-display text-sm text-white group-hover:text-gold transition-colors leading-tight line-clamp-2">
                      {r.name.trim()}
                    </div>
                    <div className="mt-auto pt-3 text-[10px] uppercase tracking-[0.2em] text-white/40">
                      Configure & price
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* TRUST STRIP */}
      <section className="bg-ink-900/40 border-t border-white/5 py-14" data-testid="product-trust">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: <ShieldCheck size={20} className="text-gold" />, ...SHOP_TRUST[0] },
              { icon: <Sparkles size={20} className="text-gold" />, ...SHOP_TRUST[1] },
              { icon: <Truck size={20} className="text-gold" />, ...SHOP_TRUST[2] },
            ].map((t) => (
              <div key={t.title} className="flex items-start gap-4">
                <div className="p-3 border border-gold/30 bg-gold/5 rounded-sm shrink-0">{t.icon}</div>
                <div>
                  <div className="font-display text-base text-white">{t.title}</div>
                  <p className="text-white/60 text-xs mt-2 leading-relaxed">{t.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}


function OptionGroup({ name, options, selected, onSelect }) {
  if (!options || options.length === 0) return null;
  const selectedName = options.find((o) => o.id === selected)?.name;
  const useDropdown = options.length > 6;
  const slug = name.toLowerCase().replace(/\s+/g, "-");

  return (
    <div data-testid={`option-group-${slug}`}>
      <div className="flex items-baseline justify-between mb-3">
        <div className="text-[11px] uppercase tracking-[0.22em] text-white/55">{name}</div>
        <div className="text-xs text-gold">{selectedName || "—"}</div>
      </div>
      {useDropdown ? (
        <select
          value={selected || ""}
          onChange={(e) => onSelect(parseInt(e.target.value, 10))}
          className="w-full bg-ink-900 border border-white/10 text-white px-4 py-3 text-sm rounded-sm outline-none focus:border-gold"
          data-testid={`select-${slug}`}
        >
          {options.map((o) => (<option key={o.id} value={o.id}>{o.name}</option>))}
        </select>
      ) : (
        <div className="flex flex-wrap gap-2">
          {options.map((o) => (
            <button key={o.id} type="button" onClick={() => onSelect(o.id)} data-testid={`option-${slug}-${o.id}`}
              className={`px-4 py-2.5 text-xs border transition-colors ${selected === o.id ? "bg-gold text-black border-gold" : "border-white/15 text-white/75 hover:border-gold hover:text-gold"}`}>
              {o.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


/* -------------------------------------------------------------------------- */
/*  ProductTabs — Details | File Prep | About                                 */
/*  Matches SinaLite's product-page layout so customers get identical         */
/*  file-prep guidance + auto-generated Setup Guide PDF downloads.            */
/* -------------------------------------------------------------------------- */

function ProductTabs({ product, catContent }) {
  const [tab, setTab] = useState("details");
  const [guides, setGuides] = useState(null);
  const [openGuide, setOpenGuide] = useState(null);  // null | "orientation" | "issues"
  const specs = useMemo(() => getProductSpecs(product), [product]);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/sinalite/products/${product.id}/guides`)
      .then(({ data }) => { if (!cancelled) setGuides(data.guides || []); })
      .catch(() => { if (!cancelled) setGuides([]); });
    return () => { cancelled = true; };
  }, [product.id]);

  return (
    <section className="max-w-7xl mx-auto px-6 md:px-10 py-16" data-testid="product-tabs">
      {/* Tab strip */}
      <div className="flex border-b border-white/10 mb-10 overflow-x-auto no-scrollbar">
        {[
          { id: "details", label: "Details", icon: <Info size={13} /> },
          { id: "fileprep", label: "File Prep", icon: <FileText size={13} /> },
          { id: "about", label: "About", icon: <Sparkles size={13} /> },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`product-tab-${t.id}`}
            className={`flex items-center gap-2 px-6 py-4 text-[11px] uppercase tracking-[0.22em] transition-colors whitespace-nowrap border-b-2 -mb-px ${tab === t.id ? "text-gold border-gold" : "text-white/50 border-transparent hover:text-white"}`}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Details tab */}
      {tab === "details" && (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,360px)] gap-10" data-testid="tab-details">
          <div>
            <div className="eyebrow gold-bar">Product details</div>
            <h2 className="font-display text-3xl md:text-4xl mt-4 tracking-tight">{product.name.trim()}</h2>
            <p className="text-white/70 text-base mt-5 leading-relaxed">{catContent.description}</p>

            <div className="mt-10">
              <div className="text-[11px] uppercase tracking-[0.22em] text-white/55 mb-4">Specifications</div>
              <div className="card-dark overflow-hidden">
                <table className="w-full text-sm">
                  <tbody>
                    {specs.map((row, i) => (
                      <tr key={row.label} className={i % 2 === 0 ? "bg-ink-900/40" : ""}>
                        <th className="text-left text-[11px] uppercase tracking-[0.18em] text-white/55 font-normal px-5 py-4 border-b border-white/5 align-top whitespace-nowrap w-[180px]">{row.label}</th>
                        <td className="text-white/85 px-5 py-4 border-b border-white/5">{row.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <aside className="space-y-4">
            {(catContent.features || []).map((f) => (
              <div key={f.title} className="card-dark p-5">
                <div className="w-9 h-9 border border-gold/30 bg-gold/5 rounded-sm flex items-center justify-center mb-3">
                  <Sparkles size={14} className="text-gold" />
                </div>
                <div className="font-display text-base text-white">{f.title}</div>
                <p className="text-white/60 text-xs mt-2 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </aside>
        </div>
      )}

      {/* File Prep tab */}
      {tab === "fileprep" && (
        <div data-testid="tab-fileprep">
          {/* 3-step process */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
            {FILE_PREP_STEPS.map((step) => (
              <div key={step.n} className="card-dark p-7 relative overflow-hidden">
                <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-gold/5" />
                <div className="relative">
                  <div className="w-10 h-10 border border-gold bg-gold/10 rounded-full flex items-center justify-center mb-5">
                    <span className="font-display text-xl text-gold">{step.n}</span>
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-gold">Step {step.n}</div>
                  <div className="font-display text-lg text-white mt-1">{step.title}</div>
                  <p className="text-white/60 text-sm mt-3 leading-relaxed">{step.body}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,380px)] gap-10">
            {/* Setup guides */}
            <div>
              <div className="flex items-baseline justify-between mb-5">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-gold">Download Setup Guide</div>
                  <h3 className="font-display text-2xl mt-2">Templates for every size</h3>
                  <p className="text-white/55 text-sm mt-2">Use these professional templates as the starting point for your artwork. Every product on our shop ships with the matching setup guide automatically.</p>
                </div>
              </div>
              {guides === null ? (
                <div className="card-dark p-6 text-white/55 text-sm flex items-center gap-3">
                  <Loader2 size={14} className="animate-spin text-gold" />
                  Loading setup guides for this product…
                </div>
              ) : guides.length === 0 ? (
                <div className="card-dark p-6 text-white/70 text-sm">
                  <PenTool size={14} className="text-gold mb-2" />
                  <div className="text-white/85 mb-2">No pre-made template for these exact sizes.</div>
                  <p className="text-white/55 text-xs leading-relaxed">
                    We recommend building your artwork with 1/16″ bleed and CMYK 300 DPI. Need a custom template?{" "}
                    <a href="mailto:torontocustomt@gmail.com" className="text-gold hover:underline">Contact our team</a>{" "}
                    and we&apos;ll build one for you.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {guides.map((g) => (
                    <a
                      key={g.url}
                      href={g.url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`setup-guide-${g.kind}-${g.title.replace(/\s+/g, "-")}`}
                      className="card-dark p-4 flex items-center gap-3 hover:border-gold/50 transition-colors group"
                    >
                      <div className="w-10 h-10 shrink-0 border border-gold/30 bg-gold/5 rounded-sm flex items-center justify-center">
                        <Download size={15} className="text-gold" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-white/40">
                          {g.kind === "rounded" ? "Rounded corners template" : g.kind === "vertical" ? "Vertical layout" : "PDF template"}
                        </div>
                        <div className="text-sm text-white group-hover:text-gold transition-colors truncate">{g.title}</div>
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 group-hover:text-gold shrink-0">.pdf</div>
                    </a>
                  ))}
                </div>
              )}

              {/* Video / help links — expandable inline guides */}
              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <FilePrepAccordionTrigger
                  eyebrow="File Orientation Guide"
                  title="How to set up proper orientation"
                  body="Ensure files back up correctly for double-sided printing."
                  open={openGuide === "orientation"}
                  onClick={() => setOpenGuide((v) => (v === "orientation" ? null : "orientation"))}
                  testId="file-prep-orientation-toggle"
                />
                <FilePrepAccordionTrigger
                  eyebrow="Common Issues"
                  title="Avoid the most common file mistakes"
                  body="Bleeds, borders, dielines, transparency & more."
                  open={openGuide === "issues"}
                  onClick={() => setOpenGuide((v) => (v === "issues" ? null : "issues"))}
                  testId="file-prep-issues-toggle"
                />
              </div>

              {/* Expanded content panels */}
              {openGuide === "orientation" && (
                <FileOrientationPanel onClose={() => setOpenGuide(null)} />
              )}
              {openGuide === "issues" && (
                <CommonIssuesPanel onClose={() => setOpenGuide(null)} />
              )}
            </div>

            {/* Guidelines checklist */}
            <aside className="card-dark p-7 self-start">
              <div className="text-[11px] uppercase tracking-[0.22em] text-gold">General file prep checklist</div>
              <h4 className="font-display text-xl text-white mt-2">Before you export</h4>
              <ul className="mt-5 space-y-3">
                {FILE_PREP_GUIDELINES.map((tip, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-white/75">
                    <CheckCircle2 size={14} className="text-gold shrink-0 mt-0.5" />
                    <span className="leading-relaxed">{tip}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-5 pt-5 border-t border-white/5 text-[11px] text-white/50 leading-relaxed">
                Not sure? Our team runs a free artwork check on every order before printing.
              </div>
            </aside>
          </div>
        </div>
      )}

      {/* About tab */}
      {tab === "about" && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-14" data-testid="tab-about">
          <div>
            <div className="eyebrow gold-bar">About this product</div>
            <h2 className="font-display text-3xl md:text-4xl mt-4 tracking-tight">{catContent.tagline}</h2>
            <p className="text-white/70 text-base mt-5 leading-relaxed">{catContent.description}</p>
            <ul className="mt-8 space-y-3">
              {[
                "Trade-quality printing at reseller pricing",
                "Free artwork review by our team",
                "Fast production — most jobs same-day",
                "Canada-wide shipping",
              ].map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-white/80">
                  <CheckCircle2 size={16} className="text-gold shrink-0 mt-0.5" /> {f}
                </li>
              ))}
            </ul>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {(catContent.features || []).map((f) => (
              <div key={f.title} className="card-dark p-6">
                <div className="w-9 h-9 border border-gold/30 bg-gold/5 rounded-sm flex items-center justify-center mb-4">
                  <Sparkles size={14} className="text-gold" />
                </div>
                <div className="font-display text-base text-white">{f.title}</div>
                <p className="text-white/60 text-xs mt-2 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  File-prep inline accordion — replaces the previous external help links.   */
/*  Kept in-file (rather than a shared component) because the copy is         */
/*  intentionally shop-specific and there's exactly one consumer.             */
/* -------------------------------------------------------------------------- */

function FilePrepAccordionTrigger({ eyebrow, title, body, open, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={open}
      data-testid={testId}
      className={`card-dark p-5 text-left flex items-start gap-4 transition-colors group ${open ? "border-gold/60 bg-gold/[0.04]" : "hover:border-gold/50"}`}
    >
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.22em] text-gold">{eyebrow}</div>
        <div className={`font-display text-base transition-colors mt-1 ${open ? "text-gold" : "text-white group-hover:text-gold"}`}>{title}</div>
        <p className="text-white/55 text-xs mt-2 leading-relaxed">{body}</p>
      </div>
      <ChevronDown
        size={16}
        className={`shrink-0 mt-1 transition-transform ${open ? "rotate-180 text-gold" : "text-white/40 group-hover:text-gold"}`}
      />
    </button>
  );
}

/** One page in the orientation diagram. The "TOP" label sits near the edge of
 * the tile that represents the top of the printed page — that edge is what
 * moves as you rotate. This mirrors the reference diagram exactly. */
function OrientationTile({ rotate = 0, tone = "light", shape = "portrait" }) {
  const bg = tone === "light" ? "bg-cyan-500" : "bg-cyan-700";
  const dim = shape === "landscape" ? "w-40 h-24" : "w-28 h-36";
  // Which edge of the tile is the "top" of the printed page + how to rotate
  // the label so it reads outward from that edge.
  const map = {
    0:    { align: "items-start justify-center pt-2", rot: 0 },
    90:   { align: "items-center justify-end pr-2",   rot: 90 },
    180:  { align: "items-end justify-center pb-2",   rot: 180 },
    "-90":{ align: "items-center justify-start pl-2", rot: -90 },
  };
  const cfg = map[String(rotate)] || map[0];
  return (
    <div className={`${bg} ${dim} flex ${cfg.align} shadow-sm`}>
      <span
        className="text-white font-black text-xl tracking-wider select-none"
        style={{ transform: `rotate(${cfg.rot}deg)`, display: "inline-block", transformOrigin: "center" }}
      >
        TOP
      </span>
    </div>
  );
}

function OrientationDiagramColumn({ title, tone, rows }) {
  const isProper = tone === "proper";
  return (
    <div className="flex-1 card-dark p-6 border border-white/10">
      <div className={`text-center text-sm md:text-base uppercase tracking-[0.2em] font-bold mb-8 ${isProper ? "text-emerald-300" : "text-rose-300"}`}>
        {title}
      </div>
      <div className="flex flex-col gap-10 items-center">
        {rows.map((row, i) => {
          const [p1, p2] = row.tiles;
          return (
            <div key={i} className="flex flex-col items-center gap-2">
              <div className="flex gap-6 text-[10px] uppercase tracking-[0.16em] text-white/60 font-medium">
                <span className={`text-center ${p1.shape === "landscape" ? "w-40" : "w-28"}`}>Page 1</span>
                <span className={`text-center ${p2.shape === "landscape" ? "w-40" : "w-28"}`}>Page 2</span>
              </div>
              <div className="flex gap-3 items-center">
                <OrientationTile rotate={p1.rot} tone="light" shape={p1.shape} />
                <OrientationTile rotate={p2.rot} tone="dark"  shape={p2.shape} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FileOrientationPanel({ onClose }) {
  // Every row = two tiles, each with its own shape & TOP-edge rotation.
  // 0 = top up, 90 = top on right (CW), 180 = top down, -90 = top on left (CCW)
  const properRows = [
    { tiles: [{ shape: "portrait",  rot: 0 }, { shape: "portrait",  rot: 0 }] },
    { tiles: [{ shape: "landscape", rot: 0 }, { shape: "landscape", rot: 0 }] },
    { tiles: [{ shape: "landscape", rot: 0 }, { shape: "landscape", rot: 90 }] },
  ];
  const incorrectRows = [
    { tiles: [{ shape: "portrait",  rot: -90 }, { shape: "portrait",  rot: -90 }] },
    { tiles: [{ shape: "portrait",  rot: 0 },   { shape: "landscape", rot: 180 }] },
    { tiles: [{ shape: "landscape", rot: -90 }, { shape: "landscape", rot: 180 }] },
  ];
  return (
    <div
      className="mt-4 card-dark p-6 md:p-8 border border-gold/30"
      data-testid="file-orientation-panel"
    >
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-gold">File Orientation Guide</div>
          <h4 className="font-display text-xl md:text-2xl mt-1 tracking-tight">How to set up proper orientation for your files</h4>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-[10px] uppercase tracking-[0.2em] text-white/50 hover:text-gold shrink-0 pt-1"
          data-testid="file-orientation-close"
        >
          Collapse
        </button>
      </div>

      <p className="text-white/70 text-sm leading-relaxed max-w-3xl">
        File orientation refers to how the artwork is placed inside your PDF. Ensure your pages are submitted so they back up properly — the top of page 1 and the top of page 2 must both point the same way — to produce the intended result.
      </p>

      <div className="mt-8">
        <div className="text-center font-display text-lg tracking-tight mb-5">Flat Artwork (Postcards, Flyers, etc.)</div>
        <div className="flex flex-col md:flex-row gap-4">
          <OrientationDiagramColumn title="Proper Page Orientation" tone="proper" rows={properRows} />
          <OrientationDiagramColumn title="Incorrect Page Orientation" tone="incorrect" rows={incorrectRows} />
        </div>
      </div>

      <p className="text-white/50 text-xs mt-6 leading-relaxed">
        Not sure how yours should back up? <Link to="/contact" className="text-gold hover:underline">Send us the file</Link> and our team will pre-flight it before we print.
      </p>
    </div>
  );
}

function CommonIssuesPanel({ onClose }) {
  const GENERAL_STEPS = [
    "Download the matching template above to guarantee correct trim, safety and bleed.",
    "DELETE any hidden layers not intended to print (setup-guide layers, cutter guides, notes).",
    "Submit each job — including multi-page projects — as a single PDF with all pages the same size.",
    "Submit with proper orientation so double-sided pieces back up correctly. See the orientation guide.",
    "Avoid tight borders in your design. If a border is too close to the trim, it may look off-centre.",
    "Include 1/8″ bleed. Keep all important art and text inside the safety margin — the more, the better.",
    "Ensure your PDF is high-res and all images are CMYK at 300 DPI.",
    "Set solid black text to C0 M0 Y0 K100. Use C30 M20 Y20 K100 for large black fills so they print rich.",
    "Embed or outline all fonts.",
    "For best colour, supply CMYK only (unless the product spec explicitly requires RGB or PMS colours).",
  ];

  return (
    <div
      className="mt-4 card-dark p-6 md:p-8 border border-gold/30"
      data-testid="common-issues-panel"
    >
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-gold">Common Issues</div>
          <h4 className="font-display text-xl md:text-2xl mt-1 tracking-tight">How to set up your files the right way</h4>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-[10px] uppercase tracking-[0.2em] text-white/50 hover:text-gold shrink-0 pt-1"
          data-testid="common-issues-close"
        >
          Collapse
        </button>
      </div>

      <div className="text-[11px] uppercase tracking-[0.22em] text-gold mb-3">General file preparation</div>
      <ol className="space-y-3 text-white/75 text-sm leading-relaxed list-decimal pl-5 marker:text-gold marker:font-semibold">
        {GENERAL_STEPS.map((step, i) => (
          <li key={i}>{step}</li>
        ))}
      </ol>

      <div className="mt-10 pt-6 border-t border-white/10">
        <div className="text-[11px] uppercase tracking-[0.22em] text-gold mb-3">Thin white text on rich black backgrounds</div>
        <p className="text-white/70 text-sm leading-relaxed">
          Small or thin white text knocked out of a rich black background is prone to colour bleeding, filling in, and poor readability from slight misregistration on press. Prep those files with the tips below.
        </p>

        <ol className="mt-5 space-y-4 text-sm text-white/75 list-decimal pl-5 marker:text-gold marker:font-semibold">
          <li>
            <span className="font-medium text-white">Use C30 M20 Y20 K100 for rich black.</span>{" "}
            Avoid heavier CMYK builds like C60 M60 Y60 K100 which increase colour-shift risk.
          </li>
          <li>
            <span className="font-medium text-white">Thicken white knockout text.</span>{" "}
            Prevent letters from filling in or becoming unreadable.
            <ul className="mt-2 space-y-1 text-white/60 text-[13px] list-disc pl-5 marker:text-white/40">
              <li>Recommended minimum line weight: <span className="text-white">0.75 pt</span></li>
              <li>Recommended minimum font size: <span className="text-white">8 pt</span></li>
            </ul>
          </li>
          <li>
            <span className="font-medium text-white">Apply swelling.</span>{" "}
            Slightly enlarging knockout areas compensates for minor ink spread on press and keeps white text crisp.
          </li>
        </ol>
      </div>

      <p className="text-white/50 text-xs mt-8 leading-relaxed">
        Still not sure? <Link to="/contact" className="text-gold hover:underline">Contact our team</Link> — we&apos;ll pre-flight your file free before it goes to print.
      </p>
    </div>
  );
}

